import { Tile as TileLayer, Vector as VectorLayer } from 'ol/layer';
import { TileWMS } from 'ol/source';
import BaseLayer from 'ol/layer/Base';
import { Geometry } from 'ol/geom';
import Feature from 'ol/Feature';
import { LayerItem, LayerType } from './layerManagement/types';

import type { IconType } from '@ais-ui/ui-core';
import {
  PiAnchorSimple,
  PiBoat,
  PiCellSignalSlash,
  PiChartPolar,
  PiCircle,
  PiCloud,
  PiFolder,
  PiGlobeHemisphereEast,
  PiGridFour,
  PiLineSegment,
  PiMapPin,
  PiNotebook,
  PiPath,
  PiPolygon,
  PiQuestion,
  PiBroadcast,
} from 'react-icons/pi';
import VectorTileLayer from 'ol/layer/VectorTile';

export const getIconFromLayerType = (layerType: LayerType): IconType => {
  switch (layerType) {
    case 'ships':
      return PiBoat;
    case 'ports':
      return PiAnchorSimple;
    case 'source-identifiers':
    case 'coverage':
      return PiChartPolar;
    case 'report':
      return PiNotebook;
    case 'point':
      return PiMapPin;
    case 'linestring':
      return PiLineSegment;
    case 'polygon':
      return PiPolygon;
    case 'circle':
      return PiCircle;
    case 'wms':
      return PiGlobeHemisphereEast;
    case 'gps-lost-grid':
      return PiCellSignalSlash;
    case 'vectortile':
      return PiGridFour;
    case 'featurecollection':
      return PiFolder;
    case 'abb-weather':
      return PiCloud;
    case 'openseamap':
      return PiAnchorSimple;
    case 'cambridge-radar':
      return PiBroadcast;
    case 'bulk-tracks':
      return PiPath;
    case 'vector-track':
      return PiPath;
    default:
      return PiQuestion;
  }
};

export const getTextFromLayerType = (layerType: LayerType): string => {
  switch (layerType) {
    case 'ships':
      return 'Layer that controls vessels';
    case 'ports':
      return 'Layer that shows all MDM ports';
    case 'source-identifiers':
      return 'Layer that shows source identifiers such as AtoNs, base stations and receivers';
    case 'report':
      return 'Report Overlay';
    case 'point':
      return 'Point';
    case 'linestring':
      return 'Line';
    case 'polygon':
      return 'Polygon';
    case 'circle':
      return 'Circle';
    case 'wms':
      return 'WMS Layer';
    case 'tile':
      return 'Tile Layer';
    case 'gps-lost-grid':
      return 'GPS Lost Grid';
    case 'vectortile':
      return 'Vector Tile';
    case 'featurecollection':
      return 'Shape Group';
    case 'abb-weather':
      return 'Weather Layer';
    case 'openseamap':
      return 'OpenSeaMap Layer';
    case 'cambridge-radar':
      return 'Radar Overlay';
    case 'bulk-tracks':
      return 'Vessel Tracks';
    case 'vector-track':
      return 'Vessel Track';
    default:
      return 'Unknown layer';
  }
};

export function getLayerTypeWithShips(layer: LayerItem): LayerType {
  if (layer.name === 'Vessels') {
    return 'ships';
  }
  if (layer.name === 'Report Overlay') {
    return 'report';
  }
  if (layer.id === 'gatehouse-ports') {
    return 'ports';
  }
  if (layer.id === 'source-identifiers') {
    return 'source-identifiers';
  }
  if (layer.name.includes('coverage ')) {
    return 'coverage';
  }
  if (layer.name.toLowerCase().includes('gps lost track line')) {
    return 'gps-lost-track-linestring';
  }
  if (layer.name.toLowerCase().includes('gps lost track point')) {
    return 'gps-lost-track-point';
  }

  if (layer.name.toLowerCase().includes('gps lost')) {
    return 'gps-lost-grid';
  }
  // Check for ABB weather layers by id prefix or properties
  if (
    layer.id.startsWith('dtn-weather-') ||
    layer.id.startsWith('abb-weather-') ||
    layer.properties?.type === 'dtn-weather' ||
    layer.properties?.type === 'abb-weather'
  ) {
    return 'abb-weather';
  }
  // Check for OpenSeaMap layer by properties
  if (layer.properties?.type === 'openseamap') {
    return 'openseamap';
  }
  // Check for Cambridge Pixel radar overlay
  if (layer.properties?.type === 'cambridge-radar') {
    return 'cambridge-radar';
  }
  // Check for Bulk Tracks layer
  if (layer.properties?.type === 'bulk-tracks') {
    return 'bulk-tracks';
  }
  // Check for Vector Track layer (ad-hoc track visualization)
  if (layer.properties?.type === 'vector-track') {
    return 'vector-track';
  }
  // Check for WMS layers by properties (set when layer is created)
  if (layer.properties?.type === 'wms' || layer.properties?.wmsLayerId) {
    return 'wms';
  }
  return getLayerType(layer.layer);
}

export function getLayerType(layer: BaseLayer): LayerType {
  if (layer instanceof VectorTileLayer) {
    return 'vectortile';
  }

  if (layer instanceof TileLayer) {
    const source = layer.getSource();
    if (source instanceof TileWMS) {
      return 'wms';
    }
    return 'tile';
  }

  if (layer instanceof VectorLayer) {
    const source = layer.getSource();
    if (!source) return 'featurecollection'; // or 'unknown', depending on intent

    const features: Feature<Geometry>[] = source.getFeatures();

    // If there are multiple features, classify as featurecollection
    if (features.length > 1) {
      return 'featurecollection';
    }

    // If there is only one feature, return its geometry type
    if (features.length === 1) {
      const geometryType = features[0].getGeometry()?.getType();
      switch (geometryType) {
        case 'Point':
          return 'point';
        case 'LineString':
          return 'linestring';
        case 'Polygon':
          return 'polygon';
        case 'Circle':
          return 'circle';
        default:
          return 'featurecollection';
      }
    }

    return 'featurecollection';
  }

  return 'unknown';
}
